/**
 * Evaluation Studio — ArgusAI
 * Manage golden datasets, run evaluations, view RAGAS metrics.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import {
  FlaskConical, Plus, Trash2, RefreshCw, Loader2, Play,
  FileText, CheckCircle, Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';

export const Route = createFileRoute('/evaluation')({
  component: EvaluationStudioPage,
});

interface Dataset { id: string; name: string; description: string | null; questionCount: number; createdAt: string; }
interface Question { id: string; question: string; expectedAnswer: string; }
interface Run { id: string; datasetId: string; status: string; totalQuestions: number; completedQuestions: number; avgFaithfulness: number | null; avgRelevancy: number | null; createdAt: string; }

function EvaluationStudioPage() {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDs, setSelectedDs] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [addQOpen, setAddQOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');

  useEffect(() => { loadDatasets(); loadRuns(); }, []);

  const loadDatasets = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<{ data: Dataset[] }>('/evaluation/datasets');
      setDatasets(res.data);
    } catch {}
    setLoading(false);
  };

  const loadRuns = async () => {
    try {
      const res = await apiClient.get<{ data: Run[] }>('/evaluation/runs');
      setRuns(res.data);
    } catch {}
  };

  const selectDataset = async (id: string) => {
    setSelectedDs(id);
    try {
      const res = await apiClient.get<{ questions: Question[] }>(`/evaluation/datasets/${id}`);
      setQuestions(res.questions);
    } catch { setQuestions([]); }
  };

  const createDataset = async () => {
    try {
      await apiClient.post('/evaluation/datasets', { name: newName, description: newDesc || undefined });
      setCreateOpen(false); setNewName(''); setNewDesc('');
      await loadDatasets();
    } catch {}
  };

  const addQuestion = async () => {
    if (!selectedDs || !newQ || !newA) return;
    try {
      await apiClient.post(`/evaluation/datasets/${selectedDs}/questions`, {
        questions: [{ question: newQ, expectedAnswer: newA }],
      });
      setAddQOpen(false); setNewQ(''); setNewA('');
      await selectDataset(selectedDs);
      await loadDatasets();
    } catch {}
  };

  const deleteDataset = async (id: string) => {
    if (!confirm('Delete this dataset and all its questions?')) return;
    try {
      await apiClient.delete(`/evaluation/datasets/${id}`);
      if (selectedDs === id) { setSelectedDs(null); setQuestions([]); }
      await loadDatasets();
    } catch {}
  };

  const selected = datasets.find(d => d.id === selectedDs);

  return (
    <div className="flex h-full">
      {/* Left: Datasets */}
      <div className="w-80 border-r flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FlaskConical className="h-5 w-5" />
              Evaluation
              <Badge variant="secondary">{datasets.length}</Badge>
            </h2>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={loadDatasets} aria-label="Refresh datasets" title="Refresh"><RefreshCw className="h-4 w-4" /></Button>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" />Dataset</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Dataset</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2"><Label>Name</Label>
                      <Input placeholder="e.g. Maintenance QA Golden Set" value={newName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)} />
                    </div>
                    <div className="space-y-2"><Label>Description</Label>
                      <Textarea placeholder="Purpose of this dataset" value={newDesc} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewDesc(e.target.value)} rows={2} />
                    </div>
                  </div>
                  <DialogFooter><Button onClick={createDataset} disabled={!newName.trim()}>Create</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div> :
              datasets.length === 0 ? <p className="text-center text-sm text-muted-foreground py-8">No datasets yet</p> :
              datasets.map(ds => (
                <button key={ds.id} onClick={() => selectDataset(ds.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${selectedDs === ds.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{ds.name}</span>
                    <Badge variant="outline" className="text-xs">{ds.questionCount} Q&A</Badge>
                  </div>
                  {ds.description && <p className="text-xs text-muted-foreground mt-1 truncate">{ds.description}</p>}
                </button>
              ))
            }
          </div>
        </ScrollArea>
      </div>

      {/* Right: Detail */}
      <div className="flex-1 overflow-auto">
        {selected ? (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{selected.name}</h2>
                <p className="text-muted-foreground">{selected.description ?? 'No description'}</p>
              </div>
              <div className="flex gap-2">
                <Dialog open={addQOpen} onOpenChange={setAddQOpen}>
                  <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" />Add Q&A</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Question</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2"><Label>Question</Label>
                        <Textarea value={newQ} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewQ(e.target.value)} rows={2} />
                      </div>
                      <div className="space-y-2"><Label>Expected Answer</Label>
                        <Textarea value={newA} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewA(e.target.value)} rows={3} />
                      </div>
                    </div>
                    <DialogFooter><Button onClick={addQuestion} disabled={!newQ || !newA}>Add</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button size="sm" onClick={async () => {
                  try {
                    await apiClient.post(`/evaluation/datasets/${selected.id}/run`, {});
                    toast.info('Evaluation started! Check back in a moment for results.');
                    setTimeout(() => loadRuns(), 5000);
                  } catch { toast.error('Failed to start evaluation. Make sure the dataset has questions.'); }
                }}>
                  <Play className="h-4 w-4 mr-1" />Run Evaluation
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteDataset(selected.id)}>
                  <Trash2 className="h-4 w-4 mr-1" />Delete
                </Button>
              </div>
            </div>
            <Separator />

            {/* Questions */}
            <Card>
              <CardHeader>
                <CardTitle>Questions ({questions.length})</CardTitle>
                <CardDescription>Golden Q&A pairs for evaluation</CardDescription>
              </CardHeader>
              <CardContent>
                {questions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No questions yet. Add some to start evaluating.</p>
                ) : (
                  <div className="space-y-3">
                    {questions.map((q, i) => (
                      <div key={q.id} className="p-3 rounded-lg bg-muted/30">
                        <div className="flex items-start gap-2">
                          <Badge variant="outline" className="text-xs mt-0.5">Q{i + 1}</Badge>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{q.question}</p>
                            <p className="text-xs text-muted-foreground mt-1"><strong>Expected:</strong> {q.expectedAnswer}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Runs */}
            <Card>
              <CardHeader>
                <CardTitle>Evaluation Runs</CardTitle>
                <CardDescription>Past evaluation results</CardDescription>
              </CardHeader>
              <CardContent>
                {runs.filter(r => r.datasetId === selected.id).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No runs yet</p>
                ) : (
                  <div className="space-y-2">
                    {runs.filter(r => r.datasetId === selected.id).map(run => (
                      <div key={run.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <Badge variant={run.status === 'completed' ? 'default' : 'secondary'}>{run.status}</Badge>
                          <span className="text-sm">{new Date(run.createdAt).toLocaleString()}</span>
                        </div>
                        <div className="flex gap-4 text-xs">
                          {run.avgFaithfulness != null && <span>Faith: {(run.avgFaithfulness * 100).toFixed(0)}%</span>}
                          {run.avgRelevancy != null && <span>Rel: {(run.avgRelevancy * 100).toFixed(0)}%</span>}
                          <span>{run.completedQuestions}/{run.totalQuestions}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <FlaskConical className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-lg font-medium">Select a dataset</p>
              <p className="text-sm">Create golden Q&A pairs and run evaluations</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
